export const TITLE = 'Apply for annual clothing allowance online';
export const SUBTITLE =
  'Application For Annual Clothing Allowance (VA Form 10-8678)';
export const FORM_ID = '10-8678';
export const MAX_BENEFIT_ITEMS = 4;

export const FORM_10_8678 = {
  INTRODUCTION: {
    FORM_TITLE: 'Apply for annual clothing allowance online',
    FORM_SUBTITLE:
      'Application For Annual Clothing Allowance (VA Form 10-8678)',
    AUTH_START_FORM_TEXT:
      'Start your application for annual clothing allowance',
    SAVE_IN_PROGRESS_TEXT:
      'You can save your progress and return to this form later.',

    INTRO:
      "Use VA Form 10-8678 to apply for a clothing allowance. You may qualify if you're a Veteran with a service-connected disability who uses a prosthetic or orthopedic device or skin medication that damages your clothing. This benefit provides money each year to help you replace damaged clothing. If you already receive a clothing allowance and you are not changing your existing award, you do not need to submit a new application each year. You can also use this form to request an additional allowance or stop your allowance if you no longer qualify.",

    WHAT_TO_KNOW_TITLE: 'What to know before filling out this form',
    WHAT_TO_KNOW_FIRST_TIME:
      'Use this form to apply for the first time or to request an additional allowance if you have new devices or skin medications.',
    WHAT_TO_KNOW_STOP_ALLOWANCE:
      'You can also use this form to stop your allowance if you no longer use a prosthetic or orthopedic device or skin medication that qualifies you for this benefit.',
    CLOTHING_ALLOWANCE_LINK:
      'Learn more about VA clothing allowance (opens in a new tab)',
    CLOTHING_ALLOWANCE_URL:
      'https://www.va.gov/disability/eligibility/special-claims/clothing-allowance/',
    PSAS_INTRO:
      'If you’d like to learn more about Rehabilitation and Prosthetic Services and the Prosthetic & Sensory Aids Service (PSAS) go to:',
    PSAS_LINK:
      'Prosthetic & Sensory Aids Service (PSAS): Clothing Allowance (opens in a new tab)',
    PSAS_URL: 'https://www.rehab.va.gov/psas/Clothing_Allowance.asp',

    GET_STARTED_TITLE: 'Follow these steps to get started',

    STEP_ELIGIBILITY_TITLE: 'Check your eligibility',
    STEP_ELIGIBILITY_INTRO:
      'Check the eligibility requirements before you apply.',

    STEP_ELIGIBILITY_LINK:
      'https://www.va.gov/disability/eligibility/special-claims/clothing-allowance/',
    STEP_ELIGIBILITY_LINK_TEXT:
      "Find out if you're eligible for clothing allowance",
    STEP_ELIGIBILITY_NOTE: 'Note:',
    STEP_ELIGIBILITY_NOTE_TEXT:
      'You must have already applied for VA disability compensation before filling out this form.',
    STEP_GATHER_TITLE: 'Gather your information',
    STEP_GATHER_INTRO: 'Here’s what you’ll need to apply:',
    STEP_GATHER_BULLETS: [
      {
        label: 'Your personal information',
        body: 'This includes your name, date of birth, contact information, and Social Security number or Military Service number.',
      },
      {
        label: 'Your service-connected condition',
        body: 'The service-connected conditions for which you use a prosthetic or orthopedic device or skin medications.',
      },
      {
        label: 'Your device or medication information',
        body: 'The name of each prosthetic or orthopedic device (like an artificial limb, brace, or wheelchair) or skin medication you use for a service-connected condition that damages or stains your clothing.',
      },
    ],

    STEP_START_TITLE: 'Start your application',
    STEP_START_BODY:
      'We’ll take you through each step of the process. It should take about 10 minutes.',
    STEP_START_FOLLOWUP:
      'You can save your progress and return to this form later.',

    AFTER_APPLY_TITLE: 'What happens after you apply',
    AFTER_APPLY_DECISION:
      'We’ll contact you by mail if we need more information. Once we process your application, we’ll mail you a letter with our decision.',
    AFTER_APPLY_NO_LETTER_P1:
      'If you don’t receive a letter from us about your request, don’t submit another request. Call us at',
    AFTER_APPLY_NO_LETTER_P2:
      'We’re here Monday through Friday, 8:00 a.m. to 8:00 p.m. ET.',
    AFTER_APPLY_PHONE_NUMBER: '8772228387',
    AFTTER_APPLY_TTY_NUMBER: '711',
  },
  BENEFIT_STATUS: {
    TITLE: 'No longer need a clothing allowance?',
    DESCRIPTION:
      'If you have previously been granted a clothing allowance but no longer are prescribed or use the prosthetic device, orthopedic appliance, or skin medication for your service-connected disability, you must notify VA.',
    QUESTION: {
      label: 'Choose your appropriate benefit status',
      body: 'Select the option to terminate this benefit if you no longer need any clothing allowance.',
    },
    TERMINATE_OPTION:
      'I elect to terminate the benefit as I no longer use any device, orthopedic appliance, or skin medication, which previously qualified me for the clothing allowance benefit pursuant to 38 USC §1162',
    TERMINATE_HINT:
      'Disclaimer: If you select this option, then all awards will be terminated. In the future, if you wish to continue receiving recurring payments, you will need to submit a new application.',
    CONTINUE_OPTION:
      'I will continue with the application for the annual clothing allowance benefit',
    ERROR_MESSAGE: 'Select appropriate benefit status',
    RESPONSIBILITY_TO_REPORT: {
      TITLE: 'Your responsibility to report changes',
      BODY: 'I understand that I must notify VA when I no longer am prescribed and use the identified items which may impact my entitlement to the annual clothing allowance benefit.',
    },
  },
  VHA_MEDICAL_FACILITY: {
    TITLE:
      'Your local VA medical facility or Prosthetic and Sensory Aids Service (PSAS) location',
    DESCRIPTION:
      'VA medical facility or PSAS information is required, even if you elected to terminate any part of your clothing allowance benefit.',

    STATE_FIELD_LABEL: 'VA medical facility or PSAS location',
    STATE_FIELD_HINT:
      'We prefilled this field based on your mailing address if it’s in a U.S. state or territory. If needed, enter or select a different state or territory.',
    STATE_FIELD_ERROR:
      'Enter or select the state or territory of the facility or PSAS.',
    STATE_FIELD_PLACEHOLDER: 'Select a state or territory',

    FIELD_LABEL:
      'Which VA medical facility or PSAS do you want to submit this application to?',
    FIELD_HINT:
      'Enter or select a VA medical facility or PSAS available for the location you entered. If the facility or PSAS isn’t listed, select “Other” and enter the name.',
    FIELD_ERROR:
      'Enter or select the facility or PSAS where you want to submit this application.',
    OTHER_LABEL: 'If "Other", please specify',
    OTHER_ERROR:
      'Enter the facility or PSAS where you want to submit this application.',
    OTHER_OPTION: 'Other',
  },
  DEVICE_APPLIANCE_MEDICATION_DETAILS: {
    ARRAY_PATH: 'deviceApplianceMedicationItems',

    NOUN_SINGULAR: 'device, appliance, or skin medication',
    NOUN_PLURAL: 'devices, appliances, and skin medications',

    INTRO_TITLE:
      'Prosthetic devices, orthopedic appliances, and skin medications',
    INTRO_DESCRIPTION: `Tell us about any device, orthopedic appliance, or skin medication you use regularly for your service-connected disability. Add 1 item at a time. You can add up to ${MAX_BENEFIT_ITEMS} items total.`,

    ITEM_DETAILS_TITLE: 'Your device, appliance, or skin medication',
    ITEM_DETAILS_DESCRIPTION: `Enter details about this item. You can add up to ${MAX_BENEFIT_ITEMS} items.`,

    SUMMARY_TITLE: 'Review your devices, appliances, and skin medications',

    SERVICE_CONNECTED_DISABILITY_TITLE:
      'What service-connected disability requires the use of this device, orthopedic appliance, or skin medication?',
    SERVICE_CONNECTED_DISABILITY_REQUIRED:
      'Enter your service-connected disability or disabilities',

    ITEM_TYPE_TITLE:
      'What is the name of this device, orthopedic appliance, or skin medication?',
    ITEM_TYPE_REQUIRED:
      'Enter the name of the device, appliance, or skin medication you use',

    ISSUING_FACILITY_STATE_TITLE:
      'In which state or territory is the Veterans Health Administration (VHA) location that issued this item?',
    ISSUING_FACILITY_STATE_HINT:
      'Enter or select a state or territory. We’ll use your selection to provide a list of VA medical facilities and PSAS locations.',
    ISSUING_FACILITY_STATE_ERROR:
      'Enter or select the state or territory where this item was issued to you',
    ISSUING_FACILITY_TITLE: 'Which VHA location issued this item?',
    ISSUING_FACILITY_HINT:
      'Enter or select a VA medical facility or PSAS from the list for the state or territory you entered. If your facility isn’t listed, select “Other” and enter the name.',
    ISSUING_FACILITY_ERROR:
      'Enter or select the facility that issued this item to you',
    ISSUING_FACILITY_OTHER_ERROR: 'Please enter a VA medical facility or PSAS',

    IMPACTED_LOCATIONS_TITLE:
      'Which area or areas of the body are impacted by this device, orthopedic appliance, or skin medication?',
    IMPACTED_LOCATIONS_REQUIRED:
      'Select at least one impacted area of the body',

    INTRO_PAGE_TITLE: 'Device, appliance, and medication details',
    ITEM_PAGE_TITLE: 'Device, appliance, or skin medication',

    INTRO_PATH: 'appliances-and-skin-medications/intro',
    SUMMARY_PATH: 'appliances-and-skin-medications/summary',
    ITEM_PATH: 'appliances-and-skin-medications/:index/details',
    ADD_ANOTHER_ITEM_REQUIRED:
      'If you have another item to add, select "Yes". If not, select "No" to continue.',
  },
  APPLICATION_CERTIFICATION: {
    TITLE: 'Application certification statement, legal information',
    DESCRIPTION:
      'Select the checkbox to certify that you regularly use a qualifying prosthetic device, orthopedic appliance, or skin medication because of a service-connected disability, and that this device or medication damages or irreparably stains your outer clothing.',
    CERTIFICATION_SECTION: {
      TITLE: 'Application certification',
      BODY: 'I hereby apply for the annual clothing allowance benefit authorized under 38 USC §1162. In doing so I certify that because of my service-connected disability or disabilities, I regularly (1) wear or use the prosthetic or orthopedic appliance(s)  identified in this application, which tends to wear out or tear my clothing; or (2) use a skin medication(s) identified in this application, which causes irreparable staining to my outergarments. Note: I understand that I must notify VA when I no longer am prescribed and use the identified items which may impact my entitlement to the annual clothing allowance benefit. By applying for this benefit, VA has the right to periodically review my continued entitlement to this benefit.',
      CHECKBOX_LABEL:
        'I have read and understand the Application certification.',
      ERROR_MESSAGE:
        'Select checkbox to confirm that you have read the application certification',
    },
    PENALTY: {
      TITLE: 'Penalty',
      BODY: 'The law provides severe penalties including fine or imprisonment, or both, for willful submission of any false statement or evidence of material fact.',
    },
  },
  TERMINATION_CERTIFICATION: {
    TITLE: 'Benefit termination confirmation and legal information',
    DESCRIPTION:
      'Confirm your intent to terminate all clothing allowance awards.',
    REAPPLICATION_DESCRIPTION:
      'In the future, if you wish to continue receiving recurring payments, you will need to submit a new application.',
    CERTIFICATION: {
      TITLE: 'Benefit termination confirmation',
      BODY: 'I elect to terminate as I no longer use a device, orthopedic appliance, or skin medication, which previously qualified me for the clothing allowance benefit pursuant to 38 USC §1162.',
      CHECKBOX_LABEL:
        'I confirm that I want to terminate all clothing allowance awards.',
      ERROR_MESSAGE:
        'Select the checkbox to confirm you are no longer prescribed or using a qualifying device, appliance, or skin medication and wish to terminate all clothing allowances',
    },
    PENALTY: {
      TITLE: 'Penalty',
      BODY: 'The law provides severe penalties including fine or imprisonment, or both, for willful submission of any false statement or evidence of material fact.',
    },
    INTENT_TO_TERMINATE: {
      TITLE: 'Intent to terminate',
      BODY: [
        'You have selected to terminate all clothing allowance awards. To continue with this option, select the "Benefit termination confirmation" checkbox.',
        'If you do not wish to terminate all clothing allowance awards, you may return to the "Benefit status" step.',
        'You will also have an opportunity to review your answers on the "Review, sign and submit" step.',
      ],
    },
  },
};

export const BODY_PART_LABELS = {
  upperRight: 'Upper body right side',
  upperLeft: 'Upper body left side',
  lowerRight: 'Lower body right side',
  lowerLeft: 'Lower body left side',
};
